import React, { useEffect, useState } from "react";
import axios from "axios";
import DataTable from "../components/DataTable";
import AdminListToolkit from "../components/listing/AdminListToolkit";
import AdminDataModal from "../components/AdminDataModal";
import { useMessageModal } from "../contexts/MessageModalContext";

const API_BASE = import.meta.env.VITE_LYRAZONE_BASE_URL;

interface RegionRow {
  id?: number;
  region_name: string;
  region_code: string;
  status: "active" | "inactive";
  created_at?: string;
  updated_at?: string;
}

const AdminRegionsPage: React.FC = () => {
  const { showModal } = useMessageModal();

  const [regions, setRegions] = useState<RegionRow[]>([]);
  const [selectedRow, setSelectedRow] = useState<RegionRow | null>(null);
  const [loading, setLoading] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"add" | "view" | "edit">("add");

  const fetchRegions = async () => {
    try {
      setLoading(true);

      const res = await fetch(`${API_BASE}/admin/regions`, {
        credentials: "include",
      });
      const result = await res.json();
      setRegions(Array.isArray(result) ? result : []);
    } catch (error) {
      console.error("Failed to fetch regions:", error);
      //   showMessage("error", "Failed to fetch regions.");
      showModal({
        type: "error",
        title: "error",
        message: "Failed to fetch regions.",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRegions();
  }, []);

  const handleAdd = () => {
    setSelectedRow({
      region_name: "",
      region_code: "",
      status: "active",
    });
    setModalMode("add");
    setModalOpen(true);
  };

  const handleView = (row: RegionRow) => {
    setSelectedRow(row);
    setModalMode("view");
    setModalOpen(true);
  };

  const handleEdit = (row: RegionRow) => {
    setSelectedRow(row);
    setModalMode("edit");
    setModalOpen(true);
  };

  const handleDelete = (row: RegionRow) => {
    if (!row?.id) {
      showModal({
        type: "info",
        title: "Info",
        message: "Please select a region first.",
        confirmText: "OK",
      });
      return;
    }

    showModal({
      type: "confirm",
      title: "Delete Region",
      message: `Are you sure you want to delete "${row.region_name}"?`,
      confirmText: "Delete",
      cancelText: "Cancel",
      onConfirm: async () => {
        try {
          await axios.delete(`${API_BASE}/admin/regions/${row.id}`, {
            withCredentials: true,
          });

          showModal({
            type: "success",
            title: "Success",
            message: "Region deleted successfully.",
            confirmText: "OK",
          });

          setSelectedRow(null);
          fetchRegions();
        } catch (error: any) {
          console.error("Delete region error:", error);

          showModal({
            type: "error",
            title: "Error",
            message:
              error?.response?.data?.message || "Failed to delete region.",
            confirmText: "OK",
          });
        }
      },
    });
  };

  const handleSubmit = async (formData: RegionRow) => {
    try {
      if (modalMode === "add") {
        await axios.post(`${API_BASE}/admin/regions`, formData, {
          withCredentials: true,
        });

        // showMessage("success", "Region added successfully.");
        showModal({
          type: "success",
          title: "Success",
          message: "Region added successfully.",
        });
      } else if (modalMode === "edit" && selectedRow?.id) {
        await axios.put(
          `${API_BASE}/admin/regions/${selectedRow.id}`,
          formData,
          { withCredentials: true },
        );

        // showMessage("success", "Region updated successfully.");
        showModal({
          type: "success",
          title: "Success",
          message: "Region updated successfully.",
        });
      }

      setModalOpen(false);
      setSelectedRow(null);
      fetchRegions();
    } catch (error) {
      console.error("Save region error:", error);
      //   showMessage("error", "Failed to save region.");
      showModal({
        type: "error",
        title: "error",
        message: "Failed to save region.",
      });
    }
  };

  const columns = [
    { key: "id", label: "ID" },
    { key: "region_name", label: "Region Name" },
    { key: "region_code", label: "Region Code" },
    { key: "status", label: "Status" },
    { key: "created_at", label: "Created At" },
  ];

  const fields = [
    {
      key: "region_name",
      label: "Region Name",
      type: "text" as const,
    },
    {
      key: "region_code",
      label: "Region Code",
      type: "text" as const,
    },
    {
      key: "status",
      label: "Status",
      type: "select" as const,
      options: [
        { label: "Active", value: "active" },
        { label: "Inactive", value: "inactive" },
      ],
    },
  ];

  return (
    <div className="admin-page">
      <AdminListToolkit
        title="Regions"
        columns={columns}
        data={regions}
        selectedRow={selectedRow}
        onAdd={handleAdd}
        onView={handleView}
        onEdit={handleEdit}
        onSoftDelete={handleDelete}
        enableExport
        exportFileName="regions"
      >
        {(filteredData) => (
          <DataTable
            columns={columns}
            data={filteredData}
            loading={loading ? "loading" : ""}
            selectedRowId={selectedRow?.id || null}
            onRowSelect={(row: RegionRow) => setSelectedRow(row)}
          />
        )}
      </AdminListToolkit>

      <AdminDataModal
        isOpen={modalOpen}
        mode={modalMode}
        title="Region"
        fields={fields}
        rowData={selectedRow || {}}
        onClose={() => {
          setModalOpen(false);
          setSelectedRow(null);
        }}
        onSubmit={handleSubmit}
      />
    </div>
  );
};

export default AdminRegionsPage;
